import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { Row, Col } from "antd";
import styles from "../FnaSection.module.css";
import localStyles from "./FnaBasicInfoPlaceholder.module.css";
import {
  basicFnaConfig,
  type FinancialSnapshot,
  type Question,
} from "./fnaBasicConfig";
import { computeSummaries, evaluateConditions } from "./fnaBasicUtils";
import { FnaField } from "./FnaField";

type Props = {
  initialData?: FinancialSnapshot;
  onSave?: (data: FinancialSnapshot) => void;
  onEditingChange?: (editing: boolean) => void;
  mode: "view" | "edit";
};

const emptySnapshot: FinancialSnapshot = {
  income: {},
  expense: {},
  currentAssets: {},
  nonCurrentAssets: {},
  liabilities: {},
  protection: {},
};

export type FnaBasicInfoHandle = {
  save: () => FinancialSnapshot;
  cancel: () => void;
  isDirty: () => boolean;
};

export const FnaBasicInfoPlaceholder = forwardRef<FnaBasicInfoHandle, Props>(function FnaBasicInfoPlaceholder(
  { initialData, onSave, onEditingChange, mode }: Props,
  ref
) {
  const headerBoxStyle: React.CSSProperties = {
    backgroundColor: "#00C8CA",

    background: "#00C8CA",
    boxShadow: "0px 1px 6px 0px rgba(222,227,234,0.6)",
    borderRadius: "6px",
    padding: "18px 24px",
    fontWeight: "500",
    fontSize: "18px",
    color: "#FFFFFF",
    lineHeight: "24px",
    display: "flex",
    justifyContent: "space-between",
  };

  const sectionBoxStyle: React.CSSProperties = {
    marginBottom: 16,
  };

  const subSectionHeaderStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
    color: "#333",
    fontSize: 14,
    fontWeight: 600,
  };

  const [snapshot, setSnapshot] = useState<FinancialSnapshot>(
    initialData ?? emptySnapshot
  );
  const [draft, setDraft] = useState<FinancialSnapshot>(snapshot);
  const [isDirty, setIsDirty] = useState(false);
  const isEditing = mode === "edit";

  useEffect(() => {
    setSnapshot(initialData ?? emptySnapshot);
    setDraft(initialData ?? emptySnapshot);
    setIsDirty(false);
  }, [initialData]);

  useEffect(() => {
    onEditingChange?.(isEditing && isDirty);
    return () => onEditingChange?.(false);
  }, [isEditing, isDirty, onEditingChange]);

  const computeDirty = (nextDraft: FinancialSnapshot) =>
    JSON.stringify(nextDraft) !== JSON.stringify(snapshot);

  const summaries = useMemo(() => computeSummaries(draft), [draft]);

  const handleValueChange = (
    question: Question,
    value: number | string | boolean | null
  ) => {
    setDraft((prev) => {
      const next: FinancialSnapshot = {
        ...prev,
        [question.section]: {
          ...(prev[question.section] as Record<string, any>),
          [question.key]: value === null ? undefined : value,
        },
      };
      setIsDirty(computeDirty(next));
      return next;
    });
  };

  const handleSave = useCallback(() => {
    setSnapshot(draft);
    onSave?.(draft);
    setIsDirty(false);
    return draft;
  }, [draft, onSave]);

  const handleCancel = useCallback(() => {
    setDraft(snapshot);
    setIsDirty(false);
  }, [snapshot]);

  useImperativeHandle(
    ref,
    () => ({
      save: handleSave,
      cancel: handleCancel,
      isDirty: () => isDirty,
    }),
    [handleSave, handleCancel, isDirty]
  );

  const renderControl = (question: Question) => {
    const value = (draft[question.section] as any)?.[question.key];
    const visible = evaluateConditions(question.visibleWhen, draft);
    if (!visible) return null;

    const isInlineSwitch =
      question.kind === "switch" && question.variant === "inlineSwitch";

    if (isInlineSwitch) {
      return (
        <Col
          span={question.colSpan ?? 24}
          key={`${question.section}-${question.key}`}
        >
          <FnaField
            question={question}
            value={value}
            mode={mode}
            onChange={(val) => handleValueChange(question, val as any)}
          />
        </Col>
      );
    }

    return (
      <Col
        span={question.colSpan ?? 12}
        className={localStyles.fieldRow}
        key={`${question.section}-${question.key}`}
      >
        <div className={localStyles.fieldLabel}>{question.label}</div>
        <FnaField
          question={question}
          value={value}
          mode={mode}
          onChange={(val) => handleValueChange(question, val as any)}
        />
      </Col>
    );
  };

  return (
    <div className={styles.fnaCard} style={{ padding: 16 }}>
      {basicFnaConfig.map((section, sectionIdx) => (
        <div key={section.title}>
          <div
            style={{
              color: "#23262C",
              fontWeight: "600",
              marginBottom: "16px",
              fontSize: "18px",
              lineHeight: "24px",
            }}
          >
            {section.title}
          </div>

          {section.blocks.map((block, blockIdx) => (
            <div key={block.key}>
              {block.headerSummaryKey && (
                <div style={{ ...headerBoxStyle, marginBottom: 16 }}>
                  <span>{block.headerLabel}</span>
                  <strong>
                    {block.headerPrefix ?? ""}
                    {summaries[block.headerSummaryKey].toLocaleString()}
                    {block.headerValueSuffix ?? ""}
                  </strong>
                </div>
              )}

              {block.groups.map((group, groupIdx) => (
                <React.Fragment key={group.key}>
                  <div style={sectionBoxStyle}>
                    <div style={subSectionHeaderStyle}>
                      <span style={{ color: "#23262C", fontSize: "16px" }}>
                        {group.title}
                      </span>
                      {group.summaryKey && (
                        <span style={{ color: "#333", fontWeight: 500 }}>
                          {group.summaryLabel ?? block.summaryLabelPrefix ?? ""}
                          {summaries[group.summaryKey].toLocaleString()}
                          {group.summaryValueSuffix ?? ""}
                        </span>
                      )}
                    </div>
                    <Row gutter={group.gutter ?? [16, 24]}>
                      {group.questions.map((q) => renderControl(q))}
                    </Row>
                  </div>
                  {groupIdx !== block.groups.length - 1 && (
                    <div
                      style={{
                        background: "#EBEBEB",
                        width: "100%",
                        height: "1px",
                        margin: "32px 0",
                      }}
                    />
                  )}
                </React.Fragment>
              ))}

              {blockIdx !== section.blocks.length - 1 && (
                <div
                  style={{
                    background: "#EBEBEB",
                    width: "100%",
                    height: "1px",
                    margin: "32px 0",
                  }}
                />
              )}
            </div>
          ))}

          {sectionIdx !== basicFnaConfig.length - 1 && (
            <div
              style={{
                background: "#EBEBEB",
                width: "100%",
                height: "1px",
                margin: "16px 0",
              }}
            />
          )}
        </div>
      ))}
    </div>
  );
});
